import {SiteBuilderDialog} from "../../sitebuilder-dialog/SiteBuilderDialog";

declare global {
  interface Window { grecaptcha?: any }
}

const getEmailLink = () => document.getElementById('email-owner-link') as HTMLAnchorElement;
const getFormContainer = () => document.getElementById('email-page-owner') as HTMLElement;
const getForm = () => getFormContainer().querySelector('form') as HTMLFormElement;
const domParser = new DOMParser();

function toggleForm() {
  const formContainer = getFormContainer();
  if (formContainer.classList.contains('hidden')) {
    formContainer.classList.remove('hidden');
    formContainer.scrollIntoView(true);
  } else {
    formContainer.classList.add('hidden');
  }
}

async function getFormContents(): Promise<Document> {
  const formUrl = getEmailLink().getAttribute('href');
  const response = await fetch(formUrl);
  if (!response.ok) {
    return Promise.reject('Could not get page contact email form');
  }
  return response.text().then(
    (responseText) => domParser.parseFromString(responseText, 'text/html')
  ).catch((e) => {
    return Promise.reject(`Could not parse page contact email form: ${e}`)
  });
}

async function postForm(): Promise<Document> {
  const formUrl = getEmailLink().getAttribute('href');
  const responsePromise = fetch(formUrl, {
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(new FormData(getForm()) as unknown as Record<string, string>),
  });
  getFormContainer().innerHTML = '<i class="fas fa-spinner-third fa-spin" aria-hidden="true"></i> Sending...';
  const response = await responsePromise;
  if (!response.ok) {
    return Promise.reject('Could not submit email page contact form');
  }
  return response.text().then(
    (responseText) => domParser.parseFromString(responseText, 'text/html')
  ).catch((e) => {
    return Promise.reject(`Could not parse response after submitting email page contact form: ${e}`)
  });
}

function setupForm(formContainer: HTMLElement) {
  formContainer.querySelector('#message-submit').addEventListener('click', (event) => {
    event.preventDefault();
    postForm().then((postResponseHTML) => {
      if (postResponseHTML.body.querySelector('h1')?.innerText === 'Success') {
        formContainer.innerHTML = '<i class="fas fa-check" aria-hidden="true"></i> Sent';
      } else {
        formContainer.innerHTML = '';
        formContainer.appendChild(postResponseHTML.body.querySelector('div'));
        const recaptchaElement = formContainer.querySelector('#emailPageOwnerAntiSpam') as HTMLElement;
        if (recaptchaElement) {
          window.grecaptcha.render(recaptchaElement, recaptchaElement.dataset.sitekey);
        }
        setupForm(formContainer); // Make sure the buttons still do what they should
      }
    });
  });

  formContainer.querySelector('#message-cancel').addEventListener('click', (event) => {
    event.preventDefault();
    toggleForm();
  });
}

async function firstTimeSetup(formContainer: HTMLElement) {
  const antiSpamElement = document.getElementById('emailPageOwnerAntiSpam');
  if (typeof window.grecaptcha !== 'undefined' && antiSpamElement) {
    // We've already loaded the recaptcha API so we need to render manually
    window.grecaptcha.render(antiSpamElement, antiSpamElement.dataset.sitekey);
  } else {
    const formHTML = await getFormContents();
    formContainer.appendChild(formHTML.body.querySelector('div'));
    if (document.getElementById('emailPageOwnerAntiSpam')) { // Have to re-get it since it may have just been appended
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.recaptcha.net/recaptcha/api.js';
      script.onerror = () => {
        SiteBuilderDialog.whenWrapperDefined(() => {
          document.querySelector<HTMLDialogElement>('#captchaFailure-emailPageOwnerAntiSpam').showModal();
        });
      }
      document.querySelector('head').appendChild(script);
    }
    setupForm(formContainer);
  }
}

export function init() {
  const formContainer = getFormContainer();
  let setup = false;

  const emailLink = getEmailLink();
  emailLink?.addEventListener('click', async (event) => {
    event.preventDefault();
    if (!setup) {
      await firstTimeSetup(formContainer);
      setup = true;
    }
    toggleForm();
  });
}
